import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@api'

export interface MergeSuggestionProfile {
  id?: string
  accountId?: string
  firstName: string
  lastName: string
  fullName: string
  avatarUrl: string
  hashtags: string[]
}

export type MergeMatchReason =
  | 'FullName'
  | 'LastName'
  | 'FirstName'
  | 'Email'
  | 'Phone'
  | 'LinkedIn'
  | 'Instagram'
  | 'Facebook'
  | 'Twitter'

export interface MergeSuggestion {
  ghostProfile: MergeSuggestionProfile & { id: string }
  realContact: MergeSuggestionProfile & { accountId: string }
  matchScore: number
  matchReasons: MergeMatchReason[]
  // Two cards of the same person: `realContact` is then the card that is
  // kept, with its id as accountId.
  isGhostPair?: boolean
}

interface GhostDuplicate {
  ghostProfile: MergeSuggestionProfile & { id: string }
  keptGhostProfile: MergeSuggestionProfile & { id: string }
  matchScore: number
  matchReasons: MergeMatchReason[]
}

interface MergeSuggestionsResponse {
  suggestions: MergeSuggestion[]
  // Sent only by a server that keeps what was typed on the merged card in a
  // private note; an older server deletes it.
  keepsGhostDetails?: boolean
  // Sent only by a server that can merge two cards of the same person.
  ghostDuplicates?: GhostDuplicate[]
}

const fetchMergeSuggestions = async (): Promise<MergeSuggestionsResponse> => {
  const { data } = await api.get<MergeSuggestionsResponse>('/ft_merge-suggestions')
  return data
}

export const useFtMergeSuggestions = () => {
  const query = useQuery<MergeSuggestionsResponse>({
    queryKey: ['ft-merge-suggestions'],
    queryFn: fetchMergeSuggestions,
  })
  // `data` stays the list of suggestions for the callers; the flag lets the
  // screen word its confirmation truthfully for the server it talks to.
  // The web app keeps this cache across reloads: one written by the previous
  // version holds the bare list under the same key.
  const raw = query.data as MergeSuggestionsResponse | MergeSuggestion[] | undefined
  // The duplicate cards come after the suggestions, in the same shape, so the
  // screen shows them in the same deck.
  const data = useMemo(() => {
    if (Array.isArray(raw)) return raw
    if (!raw?.suggestions) return undefined
    const pairs = (raw.ghostDuplicates ?? []).map(
      ({ ghostProfile, keptGhostProfile, matchScore, matchReasons }): MergeSuggestion => ({
        ghostProfile,
        realContact: { ...keptGhostProfile, accountId: keptGhostProfile.id },
        isGhostPair: true,
        matchScore,
        matchReasons,
      }),
    )
    return pairs.length > 0 ? [...raw.suggestions, ...pairs] : raw.suggestions
  }, [raw])
  return {
    ...query,
    data,
    keepsGhostDetails: !Array.isArray(raw) && raw?.keepsGhostDetails === true,
  }
}
